import React, { PropsWithChildren } from 'react';
import { OfferModalProps } from '../../services/exports/Interfaces';
import CustomModal from '../../components-library/CustomModal';
import { MODAL_SIZES, OfferKindEnum } from '../../services/exports/Constants';
import useAppMethods from '../../hooks/utility/useAppMethods';
import CloseCircleIcon from '../../assets/icons/CloseCircleIcon';
import useOfferModalTranslation from './useOfferModalTranslation';
import OfferProducts from './OfferProducts';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useScreenType from '../../hooks/utility/useScreenType';
import { useTranslation } from 'react-i18next';
import DiscountGeneralNote from '../../features/discount-exclusivity/DiscountGeneralNote';
import DiscountComparisonChoice from '../../features/discount-exclusivity/DiscountComparisonChoice';
import useActiveDiscount from '../../features/discount-exclusivity/useActiveDiscount';
import useShowExclusivityNote from '../../features/discount-exclusivity/useShowExclusivityNote';
import useDiscountDisplacement from '../../features/discount-exclusivity/useDiscountDisplacement';
import useOffersTranslations from '../../features/offers/hooks/useOffersTranslations';
import usePromoCode from '../../hooks/order/usePromoCode';
import useGiftCard from '../../features/gift-cards/hooks/useGiftCard';
import useBasket from '../../hooks/basket/useBasket';
import { useSelector } from 'react-redux';
import { selectCachedOrder } from '../../store/slices/OrderSlice';
import { selectGiftCard } from '../../store/slices/GiftCardSlice';
import { selectAppliedOffers } from '../../store/slices/OfferSlice';
import useGetBasketOfferHandlerByKind from '../../features/offers/hooks/get-offer-handler/useGetBasketOfferHandlerByKind';
import { Check } from 'lucide-react';
import { Offer } from '../../services/exports/Interfaces';

const OFFER_KIND_WITH_QUALIFYING_ITEMS: OfferKindEnum[] = [
  OfferKindEnum.PercentageOffByProducts,
  OfferKindEnum.FreeItemByProducts,
  OfferKindEnum.Bogo,
  OfferKindEnum.BogoHalfPrice,
  OfferKindEnum.AmountOffByProducts,
] as const;

const OFFER_KIND_WITH_REWARDS_ITEMS: OfferKindEnum[] = [
  OfferKindEnum.FreeItemByProducts,
  OfferKindEnum.FreeItemByMinSpendValue,
] as const;

const OFFER_KIND_WITH_QI_DESC: OfferKindEnum[] = [OfferKindEnum.FreeItemByProducts] as const;

const OfferModal = (props: OfferModalProps) => {
  const { toggleOfferModal } = useAppMethods();
  const toggleModal = () => toggleOfferModal({ openModal: false });
  const { getTitleByOffer, getDescriptionByOffer } = useOfferModalTranslation(props.offer);
  const onClose = () => {
    if (props.onClose) {
      return props.onClose();
    }

    toggleModal();
  };

  const qualifyingProducts =
    OFFER_KIND_WITH_QUALIFYING_ITEMS.includes(props.offer.kind) && props.offer.details.products;
  const rewardsProducts = OFFER_KIND_WITH_REWARDS_ITEMS.includes(props.offer.kind) && props.offer.details.free_products;
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OfferModal' });
  const qiDescription =
    OFFER_KIND_WITH_QI_DESC.includes(props.offer.kind) && t(`offers.${props.offer.kind}.qualifying_items_description`);
  const rewardDescription = t(`offers.${props.offer.kind}.reward_items_description`);

  // The comparison card ("Keep code / Use offer instead") is only meaningful when a promo/gift is
  // active AND this offer would actually apply to the current basket — otherwise "Use offer instead"
  // would remove the code but apply nothing (CTO 2026-08-03). We gate on the offer's basket profit
  // (same signal as the "Unlocked" pill), NOT isUnlocked (BOGO handlers hardcode that to true).
  // When the comparison is shown the grey general note is hidden (they'd repeat the same rule).
  const activeDiscount = useActiveDiscount();
  const basket = useBasket();
  const getOfferHandler = useGetBasketOfferHandlerByKind();
  const offerAppliesToBasket = (getOfferHandler(props.offer, basket?.items ?? [])?.profit ?? 0) > 0;
  const showComparison =
    !!activeDiscount && (activeDiscount.kind === 'promo' || activeDiscount.kind === 'gift') && offerAppliesToBasket;
  // UX point 7 (PROD-407): the grey general note only matters when applying this offer would
  // displace an active promo/gift. With nothing (or only offers/points) applied there is no
  // conflict, so hide it — an empty offer sheet shows no exclusivity message at all.
  const showGeneralNote = useShowExclusivityNote('offer');

  return (
    <WithWrapper>
      {/* On mobile the modal is full-screen; this outer div owns the scroll so a long offer (many
          qualifying items) scrolls inside the modal instead of overflowing over the page behind it.
          Padding/spacing live on the inner wrapper (a scroll container clips its own bottom padding
          on some mobile browsers) with pb-28 for bottom clearance. Desktop keeps CustomModal's own
          scroll (lg:overflow-visible). Mirrors the proven MultiPromoCodeModal layout. */}
      <div className="w-full h-full flex flex-col overflow-y-auto lg:h-auto lg:overflow-visible">
        <div className="flex flex-col p-lg space-y-lg pb-28 lg:pb-lg">
          <div className="flex items-center">
            <h3 className="text-md">{getTitleByOffer(props.offer)}</h3>
            <button className="ml-auto" onClick={onClose}>
              <CloseCircleIcon />
            </button>
          </div>
          <p className="text-muted-foreground">{getDescriptionByOffer(props.offer)}</p>
          {/* Grey general note only when the comparison card is NOT shown (otherwise they repeat
              the same one-per-order rule, CTO 2026-08-03) AND a promo/gift is active that this
              offer would displace (UX point 7 — no message when there is no conflict). */}
          {!showComparison && showGeneralNote && <DiscountGeneralNote />}
          <OfferUnlockedPill offer={props.offer} />
          {showComparison && <OfferComparisonChoice offer={props.offer} onResolved={onClose} />}
          {qualifyingProducts && (
            <OfferProducts
              offer={props.offer}
              products={qualifyingProducts}
              hasAddBtn
              title={t('qualifying_items_title')}
              description={qiDescription}
            />
          )}
          {rewardsProducts && (
            <OfferProducts
              offer={props.offer}
              products={rewardsProducts}
              title={t('reward_items_title')}
              description={rewardDescription}
              hasAddBtn
            />
          )}
        </div>
      </div>
    </WithWrapper>
  );
};

/**
 * Comparison-choice card for the offer detail modal (Inc 4, FOO-2567).
 *
 * When a promo code, gift card or points redemption is already applied it suppresses this offer
 * (full mutual exclusivity). Rather than silently block, we let the customer choose: "Use offer
 * instead" removes the active discount so `useAutoApplyBestOffer` re-applies the offer; "Keep"
 * closes the modal untouched. Before removing, we record what the offer displaced so the cart /
 * checkout can show the reverse notice ("Offer … applied. This removed CHEVERE.") with an Undo.
 */
/**
 * Green "Unlocked" status pill (PM prototype) — shown ONLY when the offer is actually applied,
 * i.e. exactly when the deals card shows "✓ Applied" (founder 2026-08-04). It must NOT show while
 * a promo/gift is active and the offer is merely applicable-but-displaced (the comparison card
 * covers that case). Mirrors the card's applied test in `useDealsItems` (offer ∈ appliedOffers) —
 * NOT basket profit, which is why it previously showed "Unlocked" alongside an active promo code.
 */
const OfferUnlockedPill = ({ offer }: { offer: Offer }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:DiscountExclusivity' });
  const appliedOffers = useSelector(selectAppliedOffers);

  const isApplied = (appliedOffers ?? []).some((o) => o?.id === offer.id);
  if (!isApplied) {
    return null;
  }

  return (
    <div
      data-test="offer-unlocked-pill"
      className="flex items-center gap-1.5 rounded-lg border-xs border-success/40 bg-success/5 px-sm py-xs text-xs font-semibold text-success"
    >
      <Check className="w-3.5 h-3.5 shrink-0" />
      {t('unlocked')}
    </div>
  );
};

const OfferComparisonChoice = ({ offer, onResolved }: { offer: Offer; onResolved: () => void }) => {
  const active = useActiveDiscount();
  const { getLabelByOffer } = useOffersTranslations();
  const { removePromoCode } = usePromoCode();
  const { remove: removeGiftCard } = useGiftCard();
  const { record } = useDiscountDisplacement();
  const { previewOfferSaving } = useBasket();
  const cachedOrder = useSelector(selectCachedOrder);
  const giftCard = useSelector(selectGiftCard);

  // Only a promo code or gift card conflicts with an offer — an offer and points coexist, so a
  // points redemption is not offered as a "use offer instead" choice here.
  if (!active || (active.kind !== 'promo' && active.kind !== 'gift')) {
    return null;
  }

  // The € this offer would save against the current basket, so BEST CHOICE can compare it to the
  // active discount. 0 when the basket has no qualifying items → shown as unknown (no badge).
  const offerSaving = previewOfferSaving(offer) || null;

  const onUseInstead = () => {
    if (active.kind === 'promo') {
      record({ kind: 'promo', label: active.label, promoCode: cachedOrder?.coupon_details?.code ?? active.label });
      removePromoCode();
    } else {
      record({ kind: 'gift', label: active.label, giftCard });
      removeGiftCard();
    }

    onResolved();
  };

  return (
    <DiscountComparisonChoice
      active={active}
      candidate={{ kind: 'offer', label: getLabelByOffer(offer), saving: offerSaving }}
      onUseInstead={onUseInstead}
      onKeep={onResolved}
      // One sentence + only "Use offer instead"; closing the modal keeps the code (CTO 2026-08-03).
      singleAction
    />
  );
};

const WithWrapper = ({ children }: PropsWithChildren) => {
  const { isMobileApp } = useMobileApp();
  const { toggleOfferModal } = useAppMethods();
  const { isDesktop } = useScreenType();
  const toggleModal = () => toggleOfferModal({ openModal: false });

  if (isMobileApp) {
    return children;
  }

  return (
    <CustomModal
      open
      position="center"
      zIndex={1300}
      size={MODAL_SIZES.LG}
      onClose={toggleModal}
      fullScreen={!isDesktop}
    >
      {children}
    </CustomModal>
  );
};

export default OfferModal;
